import { useTranslation } from 'react-i18next'

import { ChipSelectFormField } from '@/components/form/chip-select-form-field'
import { SwitchFormField } from '@/components/form/switch-form-field'
import {
  FieldBooleanBadge,
  FieldGroup,
  FieldGroupHeader,
  FieldValue,
} from '@/components/layout/field-row'
import { Badge } from '@/components/ui/badge'
import { SelectOption } from '@/components/ui/select'

import { ProviderField } from './provider-field'
import { SALTO_ACCESS_GROUPS, SaltoAccessGroupKey } from './salto-space.constants'
import { SaltoFormData, SaltoListField } from './salto-space.types'

interface SaltoAccessGroupProps {
  emptyMessage: string
  group: SaltoAccessGroupKey
  isAccessLoading: boolean
  isEditing: boolean
  levelOptions: SelectOption<string>[]
  serverValues: SaltoFormData
  zoneOptions: SelectOption<string>[]
}

export const SaltoAccessGroup = ({
  emptyMessage,
  group,
  isAccessLoading,
  isEditing,
  levelOptions,
  serverValues,
  zoneOptions,
}: SaltoAccessGroupProps) => {
  const { t } = useTranslation(['organisation'])
  const { autoAssign, levels, zones } = SALTO_ACCESS_GROUPS[group]
  const isResident = group === 'resident'

  const renderBadges = (ids: string[], options: SelectOption<string>[]) =>
    ids.length
      ? ids.map((id) => (
          <Badge key={id} color="blue" size="small" variant="light">
            {options.find((option) => option.value === id)?.label ?? id}
          </Badge>
        ))
      : null

  const renderChipSelect = (
    name: SaltoListField,
    options: SelectOption<string>[],
    placeholder: string,
  ) => (
    <FieldValue>
      <ChipSelectFormField<SaltoFormData>
        chipColor="blue"
        disabled={isAccessLoading}
        loading={isAccessLoading}
        name={name}
        options={options}
        placeholder={options.length ? placeholder : emptyMessage}
        size="small"
      />
    </FieldValue>
  )

  return (
    <FieldGroup>
      <FieldGroupHeader>
        {t(
          isResident
            ? 'organisation:places.integrations.salto.residentAccesses'
            : 'organisation:places.integrations.salto.visitorAccesses',
        )}
      </FieldGroupHeader>
      <ProviderField
        isEditing={isEditing}
        label={t(
          isResident
            ? 'organisation:places.integrations.salto.autoAssignResidentDoorAccess'
            : 'organisation:places.integrations.salto.autoAssignVisitorDoorAccess',
        )}
        view={<FieldBooleanBadge value={serverValues[autoAssign]} />}
      >
        <FieldValue>
          <SwitchFormField name={autoAssign} size="small" />
        </FieldValue>
      </ProviderField>
      <ProviderField
        isEditing={isEditing}
        label={t('organisation:places.integrations.salto.zone.label')}
        view={renderBadges(serverValues[zones], zoneOptions)}
      >
        {renderChipSelect(
          zones,
          zoneOptions,
          t('organisation:places.integrations.salto.zone.placeholder'),
        )}
      </ProviderField>
      <ProviderField
        isEditing={isEditing}
        label={t('organisation:places.integrations.salto.accessLevel.label')}
        view={renderBadges(serverValues[levels], levelOptions)}
      >
        {renderChipSelect(
          levels,
          levelOptions,
          t('organisation:places.integrations.salto.accessLevel.placeholder'),
        )}
      </ProviderField>
    </FieldGroup>
  )
}
